import { createPortal } from "react-dom";
import { X, Phone, Video, MessageSquare, Gift } from "lucide-react";
import { fmtInr } from "@/lib/api";

const PackBtn = ({ testId, onClick, top, bottom, emoji }) => (
  <button type="button" data-testid={testId} onClick={onClick} className="rounded-2xl border border-[var(--line)] bg-white hover:border-[var(--accent-c)] hover:bg-sky-50 p-2.5 text-center transition-colors">
    {emoji && <div className="text-xl leading-none mb-1">{emoji}</div>}
    <p className="text-[11px] font-bold leading-tight">{top}</p>
    <p className="font-num text-sm font-extrabold text-[var(--accent-c)] mt-0.5">{bottom}</p>
  </button>
);

export const RechargeSheet = ({ me, cfg, onClose, onPick, inCall = false }) => {
  if (!cfg) return null;
  const pick = (inr, label) => onPick({ inr, coins: inr * 6, label });
  const chatRate = cfg.chat_coins / 6;
  return createPortal(
    <div data-testid="recharge-sheet" className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/55" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 pb-7 space-y-4 fade-up max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <p className="label-caps">റീചാർജ്</p>
            <h3 className="font-display text-base md:text-lg font-extrabold">പാക്കേജ് തിരഞ്ഞെടുക്കുക</h3>
            <p className="text-[11px] text-[var(--muted-text)]">ബാലൻസ്: <span className="font-num font-bold text-[var(--accent-c)]">{fmtInr(me?.coins ?? 0)}</span>
              {(me?.bonus_coins ?? 0) > 0 && <span className="text-emerald-600 font-bold"> · ചാറ്റ് ബോണസ് {fmtInr(me.bonus_coins)}</span>}
              {inCall && " · കോൾ തടസ്സമില്ലാതെ തുടരും"}</p>
          </div>
          <button type="button" data-testid="recharge-sheet-close" onClick={onClose} className="btn-ghost w-9 h-9 rounded-full inline-flex items-center justify-center"><X size={16} /></button>
        </div>

        <div>
          <p className="label-caps flex items-center gap-1.5 mb-2"><Phone size={12} /> ഓഡിയോ കോൾ — ₹{cfg.packages.audio[0].coins / 6 / cfg.packages.audio[0].minutes}/മിനിറ്റ്</p>
          <div className="grid grid-cols-4 gap-2">
            {cfg.packages.audio.map((p) => <PackBtn key={p.minutes} testId={`recharge-pack-audio-${p.minutes}`} onClick={() => pick(p.coins / 6, `ഓഡിയോ കോൾ ${p.minutes} മിനിറ്റ്`)} top={`${p.minutes} മിനിറ്റ്`} bottom={`₹${p.coins / 6}`} />)}
          </div>
        </div>

        <div>
          <p className="label-caps flex items-center gap-1.5 mb-2"><Video size={12} /> വീഡിയോ കോൾ — ₹{cfg.packages.video[0].coins / 6 / cfg.packages.video[0].minutes}/മിനിറ്റ്</p>
          <div className="grid grid-cols-4 gap-2">
            {cfg.packages.video.map((p) => <PackBtn key={p.minutes} testId={`recharge-pack-video-${p.minutes}`} onClick={() => pick(p.coins / 6, `വീഡിയോ കോൾ ${p.minutes} മിനിറ്റ്`)} top={`${p.minutes} മിനിറ്റ്`} bottom={`₹${p.coins / 6}`} />)}
          </div>
        </div>

        <div>
          <p className="label-caps flex items-center gap-1.5 mb-2"><MessageSquare size={12} /> ചാറ്റ് — ₹{chatRate}/മെസ്സേജ്</p>
          <div className="grid grid-cols-2 gap-2">
            {[25, 50].map((n) => <PackBtn key={n} testId={`recharge-pack-chat-${n}`} onClick={() => pick(n * chatRate, `ചാറ്റ് ${n} മെസ്സേജ്`)} top={`${n} മെസ്സേജ്`} bottom={`₹${n * chatRate}`} />)}
          </div>
        </div>

        <div>
          <p className="label-caps flex items-center gap-1.5 mb-2"><Gift size={12} /> ഗിഫ്റ്റുകൾ</p>
          <div className="grid grid-cols-3 gap-2">
            {(cfg.gifts || []).map((g) => <PackBtn key={g.id} testId={`recharge-pack-gift-${g.id}`} onClick={() => pick(g.cost / 6, `ഗിഫ്റ്റ് — ${g.name}`)} emoji={g.emoji} top={g.name} bottom={`₹${g.cost / 6}`} />)}
          </div>
        </div>

        <p className="text-[10px] text-[var(--muted-text)] text-center">പാക്കേജ് തിരഞ്ഞെടുത്ത് <b className="text-emerald-700">Submit</b> അമർത്തുക — അഡ്മിൻ ചാറ്റിൽ പേമെന്റ് വിവരങ്ങൾ അയച്ചു തരും; പേമെന്റ് കഴിഞ്ഞാൽ 5 മിനിറ്റിനുള്ളിൽ ബാലൻസിൽ ക്രെഡിറ്റ് ആകും.</p>
      </div>
    </div>,
    document.body
  );
};
